import { Info, MagnifyingGlass, Plus, X } from "@phosphor-icons/react";
import { useQuery } from "convex/react";
import { useEffect, useMemo, useRef, useState } from "react";

import { api } from "../../convex/_generated/api";
import type { BusinessMembership, ConversationSummary } from "../inbox/types";
import { navigateToAppPath } from "../navigation";
import { WorkspaceLink } from "../workspace/WorkspaceLink";

type CustomerPreview = {
  id: string;
  name: string;
  emailSubscription: "Subscribed" | "Not subscribed";
  location: string;
  orders: number;
  chats: number;
  amountSpent: string;
};

const referenceCustomer: CustomerPreview = {
  id: "reference-customer",
  name: "Ntsikelelo Khuzwayo",
  emailSubscription: "Subscribed",
  location: "Soweto, Gauteng, South Africa",
  orders: 0,
  chats: 0,
  amountSpent: "R 0.00",
};

function mappedCustomers(conversations: ConversationSummary[] | undefined) {
  if (!conversations?.length) return [referenceCustomer];

  const seen = new Set<string>();
  return conversations.flatMap((conversation) => {
    if (seen.has(conversation.contactId)) return [];
    seen.add(conversation.contactId);
    const name = conversation.contact?.displayName ?? "Unnamed customer";
    return [
      {
        id: conversation.contactId,
        name,
        emailSubscription: "Not subscribed" as const,
        location: "Not available",
        orders: 0,
        chats: conversation.messageCount,
        amountSpent: "R 0.00",
      },
    ];
  });
}

export function CustomersPage({ business }: { business: BusinessMembership }) {
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const conversationsQuery = useQuery(api.conversations.listForBusiness, {
    businessId: business.businessId,
  }) as ConversationSummary[] | undefined;
  const customers = useMemo(
    () => mappedCustomers(conversationsQuery),
    [conversationsQuery],
  );

  useEffect(() => {
    if (!isImportOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsImportOpen(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isImportOpen]);

  const closeImport = () => {
    setIsImportOpen(false);
    setImportFile(null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setImportFile(file?.name.toLowerCase().endsWith(".csv") ? file : null);
  };

  return (
    <main aria-label="Customers workspace" className="customers-workspace">
      <header className="customers-header">
        <div className="customers-actions" aria-label="Customer actions">
          <button
            className="customers-button customers-button--quiet"
            type="button"
          >
            Export
          </button>
          <button
            className="customers-button customers-button--quiet"
            onClick={() => {
              setImportFile(null);
              setIsImportOpen(true);
            }}
            type="button"
          >
            Import
          </button>
          <button
            className="customers-button customers-button--primary"
            onClick={() => navigateToAppPath("/customers/new")}
            type="button"
          >
            <Plus aria-hidden="true" size={16} weight="bold" />
            Add customer
          </button>
        </div>
      </header>

      <section aria-label="Customer directory" className="customers-directory">
        <label className="customers-search">
          <MagnifyingGlass aria-hidden="true" size={22} weight="regular" />
          <input placeholder="Search customers" type="search" />
        </label>

        <div className="customers-table-wrap">
          <table className="customers-table">
            <thead>
              <tr>
                <th className="customers-table-check">
                  <input aria-label="Select all customers" type="checkbox" />
                </th>
                <th scope="col">Customer name</th>
                <th scope="col">Subscription</th>
                <th scope="col">
                  <button className="customers-sort-button" type="button">
                    Location
                  </button>
                </th>
                <th scope="col">Orders</th>
                <th scope="col">Chats</th>
                <th scope="col">Amount spent</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((customer) => (
                <tr key={customer.id}>
                  <td className="customers-table-check">
                    <input
                      aria-label={`Select ${customer.name}`}
                      type="checkbox"
                    />
                  </td>
                  <th scope="row">
                    <WorkspaceLink
                      className="customers-name-link"
                      href={`/customers/${encodeURIComponent(customer.id)}`}
                    >
                      {customer.name}
                    </WorkspaceLink>
                  </th>
                  <td>
                    {customer.emailSubscription === "Subscribed" ? (
                      <span className="customers-status customers-status--subscribed">
                        {customer.emailSubscription}
                      </span>
                    ) : (
                      <span>{customer.emailSubscription}</span>
                    )}
                  </td>
                  <td>{customer.location}</td>
                  <td>{customer.orders}</td>
                  <td>{customer.chats}</td>
                  <td>{customer.amountSpent}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {isImportOpen ? (
        <div
          className="customers-import-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeImport();
          }}
          role="presentation"
        >
          <section
            aria-labelledby="customers-import-heading"
            aria-modal="true"
            className="customers-import-modal"
            role="dialog"
          >
            <header className="customers-import-header">
              <div className="customers-import-title">
                <h2 id="customers-import-heading">Import customers by CSV</h2>
                <Info aria-label="CSV import information" size={19} />
              </div>
              <button
                aria-label="Close import dialog"
                className="customers-import-close"
                onClick={closeImport}
                type="button"
              >
                <X aria-hidden="true" size={20} />
              </button>
            </header>

            <div className="customers-import-dropzone">
              <input
                ref={fileInputRef}
                accept=".csv,text/csv"
                aria-label="Choose CSV file"
                className="customers-import-file-input"
                onChange={handleFileChange}
                type="file"
              />
              <button
                className="customers-import-add-files"
                onClick={() => fileInputRef.current?.click()}
                type="button"
              >
                Add files
              </button>
              <span>Accepts .csv</span>
              {importFile ? (
                <p className="customers-import-file-name" role="status">
                  {importFile.name}
                </p>
              ) : null}
            </div>

            <footer className="customers-import-actions">
              <a
                className="customers-import-sample"
                download="customers-sample.csv"
                href={`data:text/csv;charset=utf-8,${encodeURIComponent("firstName,lastName,email,phone\n")}`}
              >
                Download a sample CSV
              </a>
              <div className="customers-import-action-buttons">
                <button
                  className="customers-import-cancel"
                  onClick={closeImport}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className="customers-import-submit"
                  disabled={!importFile}
                  onClick={() => setIsImportOpen(false)}
                  type="button"
                >
                  Import customers
                </button>
              </div>
            </footer>
          </section>
        </div>
      ) : null}
    </main>
  );
}
